/**
 * ⟦C62 · lot 4 · friction 4⟧ **Le retour de « Laisser tourner » : le geste, la mémoire, le toast.**
 *
 * L'audit « regard neuf » du 21/10/2026 : *« Au clic sur "Jusqu'à mon tour" : rien de visible. »* Le bouton
 * vivait tout en bas de l'écran de l'Équipe, le récapitulatif en dessous, et l'arrêt (un événement, le plus
 * souvent : 24 appels sur 31 dans la partie simulée d'un délégant) tombait par-dessus. Le joueur ne savait
 * pas si la chaîne avait avancé.
 *
 * `laisserTourner()` est le SEUL chemin : le bouton de la fenêtre Actions (`BoutonLaisserTourner.tsx`) et les
 * trois vitesses de l'écran de l'Équipe l'appellent, donc rendent le même retour. La mise en mots est dans
 * `resumeDelegation.ts` (pure, testée) ; ici on garde le dernier tour de CETTE partie et on le dit au toast.
 */

import { create } from "zustand";

import { contenu } from "../../game/content";
import { useRunStore } from "../../stores/runStore";
import { useUIStore } from "../../stores/uiStore";

import { resumerDelegation, type MemoireRetour } from "./resumeDelegation";

export const useRetourEquipe = create<MemoireRetour>(() => ({ recap: null, runId: null }));

/**
 * Joue « Laisser tourner » et DIT ce qui s'est passé. Le seul chemin : le bouton de la fenêtre Actions et
 * les trois vitesses de l'écran de l'Équipe passent tous par ici, donc tous les deux rendent le même retour.
 *
 * `joursMax` absent : « Jusqu'à mon tour » (au plus `JOURS_MAX_DELEGUES` jours, la boucle s'arrête au premier
 * moment qui appartient au joueur). Rend `true` quand la boucle a tourné.
 */
export async function laisserTourner(joursMax?: number): Promise<boolean> {
  const resultat = await useRunStore
    .getState()
    .act(joursMax === undefined ? { type: "laisser_tourner" } : { type: "laisser_tourner", jusqu_a_jours: joursMax });
  const recap = resultat?.delegation;
  if (!resultat || !recap) return false;
  useRetourEquipe.setState({ recap, runId: resultat.run.runId });
  // Une partie finie (le million, la chute) a son propre écran : « C'est fini. » en toast n'ajouterait rien.
  if (recap.arret === "fin") return true;
  const resume = resumerDelegation(recap, contenu().equipe.libelles_courts?.arret);
  useUIStore.getState().toast({ texte: resume.phrase, ton: resume.ton, icone: "🎬" });
  return true;
}
